import { SortIcon } from 'components/data_table/SortIcon';
import { StyledHeaderCell } from 'components/data_table/styled';
import { TableColumn, TableProps } from 'components/data_table/types';
import { CSSProperties, Ref, useEffect, useMemo, useRef } from 'react';
import useResizeAware from 'react-resize-aware';

export type HeaderCellProps<TData> = Pick<
    TableProps<TData>,
    'sort' | 'onSortChange' | 'tableStyle'
> &
    Readonly<{
        /**
         * Column configuration.
         */
        column: TableColumn<TData>;
        /**
         * Indicates when table is in compact mode (default to `false`).
         */
        isCompact?: boolean;
        /**
         * Optional styles used for column locking to be applied to the `th` element.
         */
        lockStyles?: CSSProperties;
        /**
         * Optional ref for the `th` element.
         */
        columnRef?: Ref<HTMLTableHeaderCellElement>;
        /**
         * Optional callback for when the header cell width changes.
         */
        onResize?: () => void;
    }>;

export function HeaderCell<TData>({
    tableStyle = 'data',
    column,
    lockStyles,
    columnRef,
    sort,
    isCompact = false,
    onSortChange,
    onResize,
}: HeaderCellProps<TData>) {
    const { header, sortOn, minWidth, width, maxWidth } = column;
    const [resizeListener, { width: currentWidth }] = useResizeAware();

    const sortFields = useMemo(() => {
        if (typeof sortOn === 'string') {
            return [sortOn];
        }
        if (sortOn && sortOn.length === 0) {
            throw new Error('Missing sort fields');
        }
        return sortOn;
    }, [sortOn]);

    const sortDir = useMemo(() => {
        const firstSortField = sortFields ? sortFields[0] : undefined;
        return firstSortField
            ? sort?.find((cfg) => cfg.field === firstSortField)?.dir
            : undefined;
    }, [sort, sortFields]);

    const onResizeRef = useRef(onResize);
    onResizeRef.current = onResize;

    useEffect(() => {
        if (onResizeRef.current) {
            onResizeRef.current();
        }
    }, [currentWidth]);

    return (
        <StyledHeaderCell
            ref={columnRef}
            $tableStyle={tableStyle}
            style={{ ...lockStyles }}
        >
            {onResize && resizeListener}
            <div
                className="flex-row flex-nowrap justify-start align-center"
                style={{ minWidth, width, maxWidth }}
            >
                <div>{header({ columnInfo: column, isCompact })}</div>
                <div>
                    {sortFields && onSortChange && (
                        <SortIcon
                            fields={sortFields}
                            sortDirection={sortDir}
                            onChange={onSortChange}
                        />
                    )}
                </div>
            </div>
        </StyledHeaderCell>
    );
}
